import type { VoidFunctionComponent } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { icons, LabeledCheckbox } from '../../../../generic';
import * as ReportGenerationStore from '../../../../../redux/reportGeneration';
import styles from './SidePanel.module.less';
import TooltipIcon from '../../../../generic/TooltipIcon';
import type { ReportOptionDetail } from '../../shared/types';
import CustomHeaderModal from './CustomHeaderModal';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../../../generic/Button';
import { ReportCategories } from '../utils';
import DependencyInfoModal from './DependencyInfoModal';
import { window } from '../../../../../globals';
import utils from '../../../../../utils';
import { isFreeTier } from '../../../../../../shared/subscriptions';
import PaidFeatureGate from '../../../../generic/Gates/PaidFeatureGate';
import PaidPill from '../../../../generic/Pill/PaidPill';

type ReportOptionProps = {
  optionDetails: ReportOptionDetail;
};

/**
 * Provides a checkbox option row for the report customization panel.
 * @param {ReportOptionDetail} optionDetails - ReportOptionDetail for the provided option
 */
const ReportOption: VoidFunctionComponent<ReportOptionProps> = ({ optionDetails }) => {
  const dispatch = useDispatch();
  const { key, title, description } = optionDetails;
  const isChecked = useSelector(ReportGenerationStore.selectors.getOption(key));
  const isRestrictedOption =
    key !== 'includeDeepDependencies' &&
    (!utils.verify_subscription('premium') || isFreeTier({
      plan: window.user?.organization?.stripe_plan,
      stripePriceLimit: window.user?.organization?.stripePriceLimit,
    }));

  const onToggle = () => {
    if (isRestrictedOption) {
      window.showPlanSelectorModal();
      return;
    }

    dispatch(ReportGenerationStore.actions.setOptions({ [key]: !isChecked }));
  };

  return (
    <PaidFeatureGate isRestricted={isRestrictedOption} hidePill>
      <div className={styles.reportCustomizationPanelOptionsItem}>
        <LabeledCheckbox id={key} label={title} isChecked={isChecked || false} onChange={onToggle} />
        {isRestrictedOption && <PaidPill iconOnly />}
        {description && !isRestrictedOption && (
          <TooltipIcon id={key} tooltip={description} icon={icons.questionCircle} />
        )}
      </div>
    </PaidFeatureGate>
  );
};

/**
 * Provides the report customization panel for the report generation page.
 */
const ReportCustomizationPanel = () => {
  const category = useSelector(ReportGenerationStore.selectors.getCategory);
  const options = useSelector(ReportGenerationStore.selectors.getCurrentReportOptionDetails);
  const [shouldShowCustomHeaderModal, setShouldShowCustomHeaderModal] = useState<boolean>(false);
  const [shouldShowDependencyInfoModal, setShouldShowDependencyInfoModal] = useState<boolean>(false);

  // Don't render if there are no options for the report type and format
  if (options.length === 0) {
    return null;
  }

  return (
    <div className={styles.reportCustomizationPanel}>
      <div className="panel-heading text-center">
        Customize Report Information
      </div>
      <div className={styles.reportCustomizationPanelOptions}>
        {options.map((option) => (<ReportOption optionDetails={option} />))}
        <div className={styles.reportCustomizationPanelOptionsModals}>
          { category === ReportCategories.Licensing &&
          <Button
            onClick={() => setShouldShowCustomHeaderModal(true)}
            type={BUTTON_TYPE.SECONDARY}
            size={BUTTON_SIZE.MEDIUM}
            icon={icons.alignLeft}
            isFullWidth
          >
            Add Custom Header
          </Button>}
          <Button
            onClick={() => setShouldShowDependencyInfoModal(true)}
            type={BUTTON_TYPE.SECONDARY}
            size={BUTTON_SIZE.MEDIUM}
            icon={icons.reviewFix}
            isFullWidth
        >
            Edit Dependency Info
          </Button>
        </div>
      </div>
      <CustomHeaderModal
        shouldShowModal={shouldShowCustomHeaderModal}
        onHide={() => setShouldShowCustomHeaderModal(false)}
      />
      <DependencyInfoModal
        shouldShowModal={shouldShowDependencyInfoModal}
        onHide={() => setShouldShowDependencyInfoModal(false)}
      />
    </div>
  );
};

export default ReportCustomizationPanel;
